import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  validateLicense,
  loginWithCredentials as apiLoginWithCredentials,
} from '../api/license.api';
import { setAuthLicenseKey } from '../api/client';
import { getDeviceId } from '../utils/deviceId';
import { track } from '../utils/track';

export type UserRole = 'OWNER' | 'MEMBER';

interface AuthState {
  licenseKey: string | null;
  storeId: string | null;
  deviceId: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDemo: boolean;
  /** 'OWNER' = 라이선스 소유자(라이선스 결제/매장설정 등 전체 권한), 'MEMBER' = 추가 관리자(대기관리/메뉴설정만) */
  role: UserRole | null;
  /** 현재 로그인한 사용자의 loginId */
  loginId: string | null;
  /** MEMBER 인 경우만 — license_members.id */
  memberId: string | null;
  /** MEMBER 인 경우만 — 표시용 이름 */
  memberName: string | null;
  /** OWNER 만 가능한 작업인지 빠르게 판단하는 헬퍼 */
  isOwner: boolean;
}

interface AuthContextValue extends AuthState {
  login: (loginId: string, password: string) => Promise<void>;
  loginWithLicenseKey: (licenseKey: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY_LICENSE = 'admin_license_key';
const STORAGE_KEY_STORE = 'admin_store_id';
const STORAGE_KEY_LOGIN_ID = 'admin_login_id';
const STORAGE_KEY_ROLE = 'admin_role';
const STORAGE_KEY_MEMBER_ID = 'admin_member_id';
const STORAGE_KEY_MEMBER_NAME = 'admin_member_name';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [licenseKey, setLicenseKey] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loginId, setLoginId] = useState<string | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberName, setMemberName] = useState<string | null>(null);

  const deviceId = useMemo(() => getDeviceId(), []);

  // 앱 시작 시 ?demo=1 쿼리 감지 → 자동 로그인
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === '1') {
      setIsDemo(true);
      track('admin_demo'); // 미리보기(데모) 진입 추적
      apiLoginWithCredentials('demo', 'demo1234')
        .then((result) => {
          if (result.valid && result.storeId) {
            const key = result.licenseKey || '';
            // 데모 세션은 메모리에만 저장(영속화하지 않음)
            setLicenseKey(key);
            setAuthLicenseKey(key);
            setStoreId(result.storeId);
            setIsAuthenticated(true);
            // 데모는 OWNER 권한
            setRole('OWNER');
            setLoginId('demo');
            // URL에서 demo 쿼리 제거
            window.history.replaceState({}, '', window.location.pathname);
          }
        })
        .catch(() => { /* 실패 시 일반 로그인 화면으로 */ })
        .finally(() => setIsLoading(false));
      return;
    }

    const savedLicenseKey = localStorage.getItem(STORAGE_KEY_LICENSE);
    if (!savedLicenseKey) {
      setIsLoading(false);
      return;
    }
    // 잔존 데모 세션(login_id='demo')이 localStorage에 남아있으면 제거 후 비로그인 처리
    if (localStorage.getItem(STORAGE_KEY_LOGIN_ID) === 'demo') {
      localStorage.removeItem(STORAGE_KEY_LICENSE);
      localStorage.removeItem(STORAGE_KEY_STORE);
      localStorage.removeItem(STORAGE_KEY_LOGIN_ID);
      localStorage.removeItem(STORAGE_KEY_ROLE);
      localStorage.removeItem(STORAGE_KEY_MEMBER_ID);
      localStorage.removeItem(STORAGE_KEY_MEMBER_NAME);
      setIsLoading(false);
      return;
    }

    // 캐시된 role/loginId/memberId 복원
    const savedRole = localStorage.getItem(STORAGE_KEY_ROLE) as UserRole | null;
    const savedLoginId = localStorage.getItem(STORAGE_KEY_LOGIN_ID);
    const savedMemberId = localStorage.getItem(STORAGE_KEY_MEMBER_ID);
    const savedMemberName = localStorage.getItem(STORAGE_KEY_MEMBER_NAME);
    if (savedRole === 'OWNER' || savedRole === 'MEMBER') {
      setRole(savedRole);
    }
    setLoginId(savedLoginId);
    setMemberId(savedMemberId);
    setMemberName(savedMemberName);

    // API 요청에 즉시 인증 헤더가 실리도록 먼저 설정 (validate 응답 전 호출 대비)
    setAuthLicenseKey(savedLicenseKey);

    validateLicense(savedLicenseKey, deviceId)
      .then((result) => {
        if (result.valid && result.storeId) {
          setLicenseKey(savedLicenseKey);
          setStoreId(result.storeId);
          setIsAuthenticated(true);
          localStorage.setItem(STORAGE_KEY_STORE, result.storeId);
        } else {
          // 유효하지 않으면 저장 데이터 클리어
          setAuthLicenseKey(null);
          localStorage.removeItem(STORAGE_KEY_LICENSE);
          localStorage.removeItem(STORAGE_KEY_STORE);
          localStorage.removeItem(STORAGE_KEY_LOGIN_ID);
          localStorage.removeItem(STORAGE_KEY_ROLE);
          localStorage.removeItem(STORAGE_KEY_MEMBER_ID);
          localStorage.removeItem(STORAGE_KEY_MEMBER_NAME);
        }
      })
      .catch(() => {
        // 네트워크 오류 시 캐시된 정보로 임시 인증
        const savedStoreId = localStorage.getItem(STORAGE_KEY_STORE);
        if (savedStoreId) {
          setLicenseKey(savedLicenseKey);
          setStoreId(savedStoreId);
          setIsAuthenticated(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [deviceId]);

  // 아이디/비밀번호 로그인 (OWNER + MEMBER 모두 지원, 백엔드가 role 자동 판별)
  const login = useCallback(
    async (loginIdInput: string, password: string) => {
      const result = await apiLoginWithCredentials(loginIdInput, password);
      if (!result.valid) {
        if (result.reason === 'INVALID_CREDENTIALS') {
          throw new Error('아이디 또는 비밀번호가 올바르지 않습니다.');
        }
        throw new Error(result.reason || '로그인에 실패했습니다.');
      }
      if (!result.storeId) {
        throw new Error('매장 정보를 찾을 수 없습니다.');
      }

      const key = result.licenseKey || '';
      const userRole: UserRole = (result.role as UserRole) ?? 'OWNER';

      setLicenseKey(key);
      setAuthLicenseKey(key);
      setStoreId(result.storeId);
      setIsAuthenticated(true);
      setRole(userRole);
      setLoginId(result.loginId ?? loginIdInput);
      setMemberId(result.memberId ?? null);
      setMemberName(result.memberName ?? null);

      localStorage.setItem(STORAGE_KEY_LICENSE, key);
      localStorage.setItem(STORAGE_KEY_STORE, result.storeId);
      localStorage.setItem(STORAGE_KEY_LOGIN_ID, result.loginId ?? loginIdInput);
      localStorage.setItem(STORAGE_KEY_ROLE, userRole);
      if (result.memberId) {
        localStorage.setItem(STORAGE_KEY_MEMBER_ID, result.memberId);
      } else {
        localStorage.removeItem(STORAGE_KEY_MEMBER_ID);
      }
      if (result.memberName) {
        localStorage.setItem(STORAGE_KEY_MEMBER_NAME, result.memberName);
      } else {
        localStorage.removeItem(STORAGE_KEY_MEMBER_NAME);
      }
    },
    [],
  );

  // 라이선스 키 로그인 (기존 방식)
  const loginWithLicenseKey = useCallback(
    async (key: string) => {
      const result = await validateLicense(key, deviceId);
      if (!result.valid) {
        throw new Error(result.reason || '유효하지 않은 라이선스 키입니다.');
      }
      if (!result.storeId) {
        throw new Error('매장 정보를 찾을 수 없습니다.');
      }

      setLicenseKey(key);
      setAuthLicenseKey(key);
      setStoreId(result.storeId);
      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEY_LICENSE, key);
      localStorage.setItem(STORAGE_KEY_STORE, result.storeId);
    },
    [deviceId],
  );

  const logout = useCallback(() => {
    setLicenseKey(null);
    setAuthLicenseKey(null);
    setStoreId(null);
    setIsAuthenticated(false);
    setIsDemo(false);
    setRole(null);
    setLoginId(null);
    setMemberId(null);
    setMemberName(null);
    localStorage.removeItem(STORAGE_KEY_LICENSE);
    localStorage.removeItem(STORAGE_KEY_STORE);
    localStorage.removeItem(STORAGE_KEY_LOGIN_ID);
    localStorage.removeItem(STORAGE_KEY_ROLE);
    localStorage.removeItem(STORAGE_KEY_MEMBER_ID);
    localStorage.removeItem(STORAGE_KEY_MEMBER_NAME);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      licenseKey,
      storeId,
      deviceId,
      isAuthenticated,
      isLoading,
      isDemo,
      role,
      loginId,
      memberId,
      memberName,
      isOwner: role === 'OWNER',
      login,
      loginWithLicenseKey,
      logout,
    }),
    [
      licenseKey, storeId, deviceId, isAuthenticated, isLoading, isDemo,
      role, loginId, memberId, memberName,
      login, loginWithLicenseKey, logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
